import identity from "lodash/identity";
import React, { Suspense } from "react";
import { Route, RouterProvider, createRoutesFromElements } from "react-router";
import { createBrowserRouter } from "react-router-dom";

import DatascapeMainLayout from "./common/layouts/DatascapeMainLayout";
import { FullMapLayout } from "./common/layouts/FullMapLayout";

import RequiresAuth from "./common/components/RequiresAuth";
import MapPlayground from "./map/containers/MapPlayground";
import { MapTabs } from "./map/map-wrappers/MapViewWrapper";
import { Routes as AppRoutes } from "./navigation/redux/model";
import { SignInUpLayout } from "./user/components/SignInUpLayout";
import { AvailableDataTabs } from "./available-data/pages/AvailableDataPage";

const ErrorPage = React.lazy(() => import("./common/components/ErrorPage"));
const HomePage = React.lazy(() => import("./home/views/HomePage"));

const CreateOrManageMapTemplatePage = React.lazy(
    () => import("./map-templates/pages/CreateOrManageMapTemplatePage")
);
const ManageSavedViewPage = React.lazy(
    () => import("./map-templates/pages/ManageSavedViewPage")
);
const MapTemplatesPage = React.lazy(
    () => import("./map-templates/pages/MapTemplatesPage")
);
const ViewAllMapTemplateSavedViewsPage = React.lazy(
    () => import("./map-templates/pages/ViewAllMapTemplateSavedViewsPage")
);
const FullMapContainer = React.lazy(
    () => import("./map/containers/FullMapContainer")
);
const MarketplaceContainer = React.lazy(
    () => import("./marketplace/containers/MarketplaceContainer")
);
const VelocityApiKeyPage = React.lazy(
    () => import("./marketplace/pages/VelocityApiKeyPage")
);
const BGVelocityUnlocked = React.lazy(
    () => import("./marketplace/views/BGVelocityUnlocked")
);

const SignInContainer = React.lazy(
    () => import("./user/containers/SignInContainer")
);
const SignUpContainer = React.lazy(
    () => import("./user/containers/SignUpContainer")
);

const SettingsPage = React.lazy(() => import("./settings/pages/SettingsPage"));

const EgressPurchasedDatasetPage = React.lazy(
    () => import("./egress/pages/EgressPurchasedDataPage")
);

const ExportGeneratedFilePage = React.lazy(
    () => import("./egress/pages/ExportGeneratedFilePage")
);

const EgressMyDataPage = React.lazy(
    () => import("./egress/pages/EgressMyDataPage")
);
const EgressRedirectPage = React.lazy(
    () => import("./egress/pages/EgressRedirectPage")
);

const AvailableDataPage = React.lazy(
    () => import("./available-data/pages/AvailableDataPage")
);

const AUTHED_ROUTES: JSX.Element[] = [
    <Route
        key="*"
        path="*"
        element={
            <RequiresAuth>
                <DatascapeMainLayout>
                    <Suspense fallback={<div />}>
                        <ErrorPage
                            title="Sorry, this page doesn't exist!"
                            subtitle="It appears the page you're looking for is no longer available."
                        />
                    </Suspense>
                </DatascapeMainLayout>
            </RequiresAuth>
        }
    />,
    <Route
        key={AppRoutes.home}
        path={AppRoutes.home}
        element={
            <RequiresAuth>
                <DatascapeMainLayout>
                    <Suspense fallback={<div />}>
                        <HomePage />
                    </Suspense>
                </DatascapeMainLayout>
            </RequiresAuth>
        }
    />,
    <Route
        key={AppRoutes.signIn}
        path={AppRoutes.signIn}
        element={
            <SignInUpLayout>
                <Suspense fallback={<div />}>
                    <SignInContainer />
                </Suspense>
            </SignInUpLayout>
        }
    />,
    <Route
        key={AppRoutes.signUp}
        path={AppRoutes.signUp}
        element={
            <SignInUpLayout>
                <Suspense fallback={<div />}>
                    <SignUpContainer />
                </Suspense>
            </SignInUpLayout>
        }
    />,
    <Route
        key={`${AppRoutes.mapView}/:mapTemplateId/map`}
        path={`${AppRoutes.mapView}/:mapTemplateId/map`}
        element={
            <RequiresAuth>
                <FullMapLayout>
                    <Suspense fallback={<div />}>
                        <FullMapContainer currentTab={MapTabs.map} />
                    </Suspense>
                </FullMapLayout>
            </RequiresAuth>
        }
    />,
    <Route
        key={`${AppRoutes.mapView}/:mapTemplateId/configuration`}
        path={`${AppRoutes.mapView}/:mapTemplateId/configuration`}
        element={
            <RequiresAuth>
                <FullMapLayout>
                    <Suspense fallback={<div />}>
                        <FullMapContainer currentTab={MapTabs.configuration} />
                    </Suspense>
                </FullMapLayout>
            </RequiresAuth>
        }
    />,
    <Route
        key={`${AppRoutes.mapView}/:mapTemplateId/saved-views`}
        path={`${AppRoutes.mapView}/:mapTemplateId/saved-views`}
        element={
            <RequiresAuth>
                <FullMapLayout>
                    <Suspense fallback={<div />}>
                        <FullMapContainer currentTab={MapTabs.savedViews} />
                    </Suspense>
                </FullMapLayout>
            </RequiresAuth>
        }
    />,
    <Route
        key={`${AppRoutes.mapView}/:mapTemplateId/configuration/:savedAreaId`}
        path={`${AppRoutes.mapView}/:mapTemplateId/configuration/:savedAreaId`}
        element={
            <RequiresAuth>
                <FullMapLayout>
                    <Suspense fallback={<div />}>
                        <FullMapContainer
                            currentTab={MapTabs.configuration}
                            isSavedAreaForm
                        />
                    </Suspense>
                </FullMapLayout>
            </RequiresAuth>
        }
    />,
    <Route
        key={`${AppRoutes.mapTemplates}/:tab?`}
        path={`${AppRoutes.mapTemplates}/:tab?`}
        element={
            <RequiresAuth>
                <DatascapeMainLayout>
                    <Suspense fallback={<div />}>
                        <MapTemplatesPage />
                    </Suspense>
                </DatascapeMainLayout>
            </RequiresAuth>
        }
    />,
    <Route
        key={`${AppRoutes.mapTemplatesCreate}/:dataSourceId?`}
        path={`${AppRoutes.mapTemplatesCreate}/:dataSourceId?`}
        element={
            <RequiresAuth>
                <DatascapeMainLayout>
                    <Suspense fallback={<div />}>
                        <CreateOrManageMapTemplatePage />
                    </Suspense>
                </DatascapeMainLayout>
            </RequiresAuth>
        }
    />,
    <Route
        key={`${AppRoutes.mapTemplateSavedViews}/:id`}
        path={`${AppRoutes.mapTemplateSavedViews}/:id`}
        element={
            <RequiresAuth>
                <DatascapeMainLayout>
                    <Suspense fallback={<div />}>
                        <ViewAllMapTemplateSavedViewsPage />
                    </Suspense>
                </DatascapeMainLayout>
            </RequiresAuth>
        }
    />,
    <Route
        key={`${AppRoutes.mapTemplateSavedViews}/manage/:id`}
        path={`${AppRoutes.mapTemplateSavedViews}/manage/:id`}
        element={
            <RequiresAuth>
                <DatascapeMainLayout>
                    <Suspense fallback={<div />}>
                        <ManageSavedViewPage />
                    </Suspense>
                </DatascapeMainLayout>
            </RequiresAuth>
        }
    />,
    <Route
        key={`${AppRoutes.mapTemplatesManage}/:id`}
        path={`${AppRoutes.mapTemplatesManage}/:id`}
        element={
            <RequiresAuth>
                <DatascapeMainLayout>
                    <Suspense fallback={<div />}>
                        <CreateOrManageMapTemplatePage />
                    </Suspense>
                </DatascapeMainLayout>
            </RequiresAuth>
        }
    />,
    <Route
        key={`${AppRoutes.velocityApiKey}`}
        path={`${AppRoutes.velocityApiKey}`}
        element={
            <RequiresAuth>
                <DatascapeMainLayout>
                    <Suspense fallback={<div />}>
                        <VelocityApiKeyPage />
                    </Suspense>
                </DatascapeMainLayout>
            </RequiresAuth>
        }
    />,
    <Route
        key={`${AppRoutes.unlocked}`}
        path={`${AppRoutes.unlocked}`}
        element={
            <RequiresAuth>
                <DatascapeMainLayout>
                    <Suspense fallback={<div />}>
                        <BGVelocityUnlocked />
                    </Suspense>
                </DatascapeMainLayout>
            </RequiresAuth>
        }
    />,
    <Route
        key={`${AppRoutes.explore}/:menuItem?`}
        path={`${AppRoutes.explore}/:menuItem?`}
        element={
            <RequiresAuth>
                <DatascapeMainLayout>
                    <Suspense fallback={<div />}>
                        <MarketplaceContainer />
                    </Suspense>
                </DatascapeMainLayout>
            </RequiresAuth>
        }
    />,
    <Route
        key={AppRoutes.profileSettings}
        path={AppRoutes.profileSettings}
        element={
            <RequiresAuth>
                <DatascapeMainLayout>
                    <Suspense fallback={<div />}>
                        <SettingsPage tab="Profile" />
                    </Suspense>
                </DatascapeMainLayout>
            </RequiresAuth>
        }
    />,
    <Route
        key={AppRoutes.organizationSettings}
        path={AppRoutes.organizationSettings}
        element={
            <RequiresAuth ownerOnly>
                <DatascapeMainLayout>
                    <Suspense fallback={<div />}>
                        <SettingsPage tab="Organization" />
                    </Suspense>
                </DatascapeMainLayout>
            </RequiresAuth>
        }
    />,
    <Route
        key={AppRoutes.myData}
        path={AppRoutes.myData}
        element={
            <RequiresAuth>
                <DatascapeMainLayout>
                    <Suspense fallback={<div />}>
                        <AvailableDataPage
                            currentTab={AvailableDataTabs.myData}
                        />
                    </Suspense>
                </DatascapeMainLayout>
            </RequiresAuth>
        }
    />,

    <Route
        key={`${AppRoutes.myData}/egress`}
        path={`${AppRoutes.myData}/egress`}
        element={
            <RequiresAuth>
                <Suspense fallback={<div />}>
                    <EgressRedirectPage />
                </Suspense>
            </RequiresAuth>
        }
    />,
    <Route
        key={`${AppRoutes.myData}/egress/:id`}
        path={`${AppRoutes.myData}/egress/:id`}
        element={
            <RequiresAuth>
                <Suspense fallback={<div />}>
                    <EgressMyDataPage />
                </Suspense>
            </RequiresAuth>
        }
    />,
    <Route
        key={`${AppRoutes.purchasedData}/egress/:id`}
        path={`${AppRoutes.purchasedData}/egress/:id`}
        element={
            <RequiresAuth>
                <Suspense fallback={<div />}>
                    <EgressPurchasedDatasetPage />
                </Suspense>
            </RequiresAuth>
        }
    />,
    <Route
        key={`${AppRoutes.myData}/export/:id`}
        path={`${AppRoutes.myData}/export/:id`}
        element={
            <RequiresAuth>
                <Suspense fallback={<div />}>
                    <ExportGeneratedFilePage />
                </Suspense>
            </RequiresAuth>
        }
    />,
    <Route
        key={AppRoutes.generatedFiles}
        path={AppRoutes.generatedFiles}
        element={
            <RequiresAuth>
                <DatascapeMainLayout>
                    <Suspense fallback={<div />}>
                        <AvailableDataPage
                            currentTab={AvailableDataTabs.generatedFiles}
                        />
                    </Suspense>
                </DatascapeMainLayout>
            </RequiresAuth>
        }
    />,
    <Route
        key={AppRoutes.purchasedData}
        path={AppRoutes.purchasedData}
        element={
            <RequiresAuth>
                <DatascapeMainLayout>
                    <Suspense fallback={<div />}>
                        <AvailableDataPage
                            currentTab={AvailableDataTabs.availableData}
                        />
                    </Suspense>
                </DatascapeMainLayout>
            </RequiresAuth>
        }
    />,
    <Route
        key={AppRoutes.mapPlayground}
        path={AppRoutes.mapPlayground}
        element={
            <RequiresAuth>
                <FullMapLayout>
                    <Suspense fallback={<div />}>
                        <MapPlayground />
                    </Suspense>
                </FullMapLayout>
            </RequiresAuth>
        }
    />,
];

const useRoutes = () => {
    return AUTHED_ROUTES;
};

const Routes = () => {
    const routesList = useRoutes();

    const RoutesComponent = <>{routesList.map(identity)}</>;

    const router = createBrowserRouter(
        createRoutesFromElements(RoutesComponent)
    );

    return <RouterProvider router={router} />;
};

export default Routes;
